import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './Dashboard.css';
import GenrePicker from '../components/GenrePicker';
import ArticlePreview from '../components/ArticlePreview';
import ArticleEditor, { buildPreviewHTML, imgsMissingAlt } from '../components/ArticleEditor';
import SettingsPanel from '../components/SettingsPanel';
import { uploadImage } from '../lib/uploadImage';
import DraftPreview from '../components/DraftPreview';
import orangeLogo from '../assets/orangelog.png';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  white:   '#fff',
};

function AuthorDashboard() {
  const navigate = useNavigate();
  const { companyName, username } = useParams();

  const { accessToken: token, clearSession } = useAuth();
  const userType    = localStorage.getItem('userType');
  const storedUser  = localStorage.getItem('username');
  const displayName = localStorage.getItem('displayName') || storedUser;
  const storedCo    = localStorage.getItem('companyName');

  useEffect(() => {
    if (!token || userType !== 'author') { navigate('/login'); return; }
    if (
      decodeURIComponent(username) !== storedUser ||
      decodeURIComponent(companyName) !== storedCo
    ) {
      navigate('/login');
    }
  }, []);

  const [articles, setArticles]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');
  const [showForm, setShowForm]               = useState(false);
  const [editingArticle, setEditingArticle]   = useState(null); // article being edited
  const [genreTree, setGenreTree]             = useState({ main: '', sub: '' });
  const [genrePickerKey, setGenrePickerKey]   = useState(0); // force remount on open
  const [submittedArticle, setSubmittedArticle] = useState(null);
  const [previewArticle, setPreviewArticle]   = useState(null); // { _id, ... } to preview
  const [showDraftPreview, setShowDraftPreview] = useState(false);
  const [formData, setFormData] = useState({
    title: '', writer_name: '', content: '',
    audience: 'local', token_cost: 4, headline_image: null,
    external_url: '',
  });
  const [formMode, setFormMode] = useState('article'); // 'article' | 'external'
  const [pricingMode, setPricingMode]   = useState('manual');
  const [autoAdjust, setAutoAdjust]     = useState({ firstDecreaseDays: '', decreasePercent: '', frequencyDays: '', skipIfDailyAbove: '' });
  const [editNote, setEditNote]         = useState('');
  const [followerCount, setFollowerCount] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get('/api/authors/me/follower-count', { headers: authHeader })
      .then(res => setFollowerCount(res.data.followerCount))
      .catch(() => {});
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/authors/me/articles', { headers: authHeader });
      setArticles(res.data);
    } catch (err) {
      setError('Failed to load articles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const openEditForm = (article) => {
    setEditingArticle(article);
    setFormData({
      title:       article.title,
      writer_name: Array.isArray(article.writer_name) ? article.writer_name.join(', ') : (article.writer_name || ''),
      content:     article.content || '',
      audience:    article.audience || 'national',
      token_cost:  article.token_cost ?? 4,
    });
    const adj = article.autoAdjust || {};
    const daysFromNow = adj.firstDecreaseDate
      ? Math.max(1, Math.round((new Date(adj.firstDecreaseDate) - Date.now()) / 86400000))
      : '';
    setPricingMode(article.pricingMode || 'manual');
    setAutoAdjust({
      firstDecreaseDays: adj.firstDecreaseDate  ? String(daysFromNow)               : '',
      decreasePercent:   adj.decreasePercent  != null ? String(adj.decreasePercent)  : '',
      frequencyDays:     adj.frequencyDays    != null ? String(adj.frequencyDays)    : '',
      skipIfDailyAbove:  adj.skipIfDailyAbove != null ? String(adj.skipIfDailyAbove) : '',
    });
    setGenreTree(article.genre_tree || { main: '', sub: '' });
    setGenrePickerKey(k => k + 1); // remount GenrePicker with new initialValue
    setEditNote('');
    setShowForm(true);
    setSubmittedArticle(null);
    setError('');
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setError('');
    if (!genreTree.main) { setError('Please select a genre category.'); return; }

    const rawCost = parseInt(formData.token_cost, 10);
    if (isNaN(rawCost) || rawCost < 4 || rawCost > 150) {
      setError('Article credit price must be between 4 and 150.');
      return;
    }
    if (!saveAsDraft) {
      const missing = imgsMissingAlt(formData.content);
      if (missing > 0) {
        setError(`${missing} image${missing > 1 ? 's are' : ' is'} missing alt text. Click each image in the editor and add an accessibility description before publishing.`);
        return;
      }
    }
    const payload = {
      writer_name: formData.writer_name
        ? formData.writer_name.split(',').map(n => n.trim()).filter(n => n)
        : [displayName],
      title:       formData.title,
      content:     formData.content,
      genre_tree:  genreTree,
      audience:    formData.audience,
      token_cost:  rawCost,
      headline_image: formData.headline_image || null,
      ...(formMode === 'external' ? { external_url: formData.external_url } : {}),
      pricingMode,
      autoAdjust: pricingMode === 'auto' ? {
        firstDecreaseDate: new Date(Date.now() + Number(autoAdjust.firstDecreaseDays) * 86400000),
        decreasePercent:   Number(autoAdjust.decreasePercent),
        frequencyDays:     Number(autoAdjust.frequencyDays),
        skipIfDailyAbove:  autoAdjust.skipIfDailyAbove !== '' ? Number(autoAdjust.skipIfDailyAbove) : null,
      } : {},
      ...(saveAsDraft ? { status: 'draft' } : {}),
    };

    try {
      if (editingArticle) {
        const editPayload = editingArticle.purchased > 0 ? { ...payload, editNote } : payload;
        await axios.patch(`/api/articles/${editingArticle._id}`, editPayload, { headers: authHeader });
        setShowForm(false);
        setEditingArticle(null);
        setEditNote('');
        fetchArticles();
      } else {
        // New article
        const articleData = {
          ...payload,
          article_id: `${decodeURIComponent(companyName).replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`,
        };
        const res = await axios.post('/api/articles', articleData, { headers: authHeader });
        setSubmittedArticle(res.data);
        setShowForm(false);
        fetchArticles();
      }
      setFormData({ title: '', writer_name: '', content: '', audience: 'local', token_cost: 4, headline_image: null });
      setPricingMode('manual');
      setAutoAdjust({ firstDecreaseDays: '', decreasePercent: '', frequencyDays: '', skipIfDailyAbove: '' });
      setGenreTree({ main: '', sub: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save article.');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingArticle(null);
    setEditNote('');
    setError('');
    setFormMode('article');
  };



  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F' }} />
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F' }} />
          <div style={{ gridColumn: 2, gridRow: 2, background: '#13273F', borderBottom: '2px solid #000' }} />
        </nav>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const STATUS_COLOR = { draft: '#dc3545', pending: '#e67e22', approved: '#28a745', rejected: '#dc3545' };
  const STATUS_LABEL = { draft: 'DRAFT', pending: 'Pending Review', approved: 'Live', rejected: 'Rejected' };

  return (
    <div className="dashboard-container">
      <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
        {/* Logo — spans both rows */}
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
          <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
        </div>

        {/* Center: dashboard title */}
        <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
              {decodeURIComponent(companyName)}
            </div>
            <div style={{ color: T.white, fontSize: '28px', fontWeight: '300', fontFamily: 'Arial, sans-serif', letterSpacing: '0.04em', lineHeight: 1 }}>
              Author Dashboard
            </div>
          </div>
        </div>

        {/* Right: user info + logout — spans both rows */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#13273F' }}>
          <div style={{ color: T.white, fontSize: '15px', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}>{displayName}</div>
          <div style={{ color: '#aaa', fontSize: '11px', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Author</div>
          <button
            onClick={handleLogout}
            style={{ marginTop: '4px', padding: '6px 16px', background: 'transparent', border: '1px solid #3a3a3a', borderRadius: '5px', color: '#ccc', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}
          >
            Log Out
          </button>
        </div>

        {/* Row 2: nav strip */}
        <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', background: '#13273F', borderBottom: '2px solid #000' }}>
          <Link
            to="/"
            style={{ color: '#aaa', textDecoration: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 20px', height: '100%', display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', transition: 'color 0.12s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#aaa'; }}
          >
            ← PubSilo Home
          </Link>
        </div>
      </nav>

      <div className="dashboard-wrapper">
        {/* Left Sidebar */}
        <div className="sidebar sidebar-left">
          <div className="sidebar-title">Author Info</div>
          <div className="stats">
            <div className="stat-item">
              <div className="stat-value">{displayName}</div>
              <div className="stat-label">Author</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ fontSize: '0.95rem', color: '#666' }}>{decodeURIComponent(companyName)}</div>
              <div className="stat-label">Company</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{articles.length}</div>
              <div className="stat-label">Articles Submitted</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#28a745' }}>{articles.filter(a => a.status === 'approved').length}</div>
              <div className="stat-label">Live</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#007bff' }}>{followerCount !== null ? followerCount : '—'}</div>
              <div className="stat-label">Followers</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#e67e22' }}>{articles.filter(a => a.status === 'pending').length}</div>
              <div className="stat-label">Pending Review</div>
            </div>
            {articles.some(a => a.status === 'draft') && (
              <div className="stat-item">
                <div className="stat-value" style={{ color: '#dc3545' }}>{articles.filter(a => a.status === 'draft').length}</div>
                <div className="stat-label">Drafts</div>
              </div>
            )}
            {articles.some(a => a.status === 'rejected') && (
              <div className="stat-item">
                <div className="stat-value" style={{ color: '#dc3545' }}>{articles.filter(a => a.status === 'rejected').length}</div>
                <div className="stat-label">Needs Revision</div>
              </div>
            )}
            <div className="stat-item">
              <div className="stat-value">{articles.reduce((s, a) => s + (a.purchased || 0), 0)}</div>
              <div className="stat-label">Total Purchases</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#7b2ff7' }}>{articles.reduce((s, a) => s + (a.kudos || 0), 0)}</div>
              <div className="stat-label">Content Kudos</div>
            </div>
          </div>
          <SettingsPanel onLogout={handleLogout} />
        </div>

        {/* Center */}
        <div className="main-content">

          {submittedArticle ? (() => {
            const articleUrl = `${window.location.origin}/${encodeURIComponent(decodeURIComponent(companyName))}?a=${submittedArticle._id}`;
            const embedCode  = `<a href="${articleUrl}">PubSilo (${submittedArticle.token_cost} tokens)</a>`;
            return (
              <div className="article-share-card">
                <h3>Article Submitted!</h3>
                {submittedArticle.status === 'pending' ? (
                  <p style={{ color: '#e67e22', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Your article has been submitted for editor review. Once approved it will appear here and be visible to readers.
                  </p>
                ) : (
                  <p style={{ color: '#28a745', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Your article is live and visible to readers.
                  </p>
                )}
                <p style={{ marginBottom: '0.25rem' }}>Article URL{submittedArticle.status === 'pending' ? ' (live after approval)' : ''}:</p>
                <div className="share-link" style={{ marginBottom: '1rem' }}>
                  <a href={articleUrl} target="_blank" rel="noopener noreferrer">{articleUrl}</a>
                </div>
                <p style={{ marginBottom: '0.25rem' }}>Embed code:</p>
                <div style={{
                  backgroundColor: '#f4f4f4', border: '1px solid #ddd', borderRadius: '6px',
                  padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.9rem',
                  wordBreak: 'break-all', userSelect: 'all', marginBottom: '1rem',
                }}>
                  {embedCode}
                </div>
                <button onClick={() => setSubmittedArticle(null)}>Back to Dashboard</button>
              </div>
            );
          })() : showForm ? (
            <div className="article-form-card">
              <h3>{editingArticle ? 'Edit Article' : 'Create New Article'}</h3>
              {editingArticle && (
                <div style={{ padding: '0.65rem 0.9rem', backgroundColor: '#fff8f0', border: '1px solid #f5c89a', borderRadius: '6px', marginBottom: '1.25rem', fontSize: '0.85rem', color: '#a05000' }}>
                  Editing <strong>"{editingArticle.title}"</strong>
                  {editingArticle.purchased > 0 && ' — this article has been purchased by readers.'}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                {/* Mode toggle — only shown when creating (not editing) */}
                {!editingArticle && (
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', padding: '0.25rem', background: '#f0f0f0', borderRadius: '7px', width: 'fit-content' }}>
                    {[{ id: 'article', label: '✍️ Full Article' }, { id: 'external', label: '🔗 External Link' }].map(m => (
                      <button key={m.id} type="button" onClick={() => setFormMode(m.id)}
                        style={{ padding: '0.4rem 1rem', border: 'none', borderRadius: '5px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', background: formMode === m.id ? '#1a1a1a' : 'transparent', color: formMode === m.id ? '#fff' : '#666', transition: 'background 0.15s' }}
                      >{m.label}</button>
                    ))}
                  </div>
                )}
                {formMode === 'external' && !editingArticle && (
                  <div className="form-group">
                    <label>External URL <span style={{ color: '#dc3545' }}>*</span></label>
                    <input type="url" name="external_url" value={formData.external_url} onChange={handleFormChange} required placeholder="https://yoursite.com/article-slug" />
                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.3rem' }}>Readers will be sent to this URL after purchasing access.</div>
                  </div>
                )}
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" name="title" value={formData.title} onChange={handleFormChange} required />
                </div>
                <div className="form-group">
                  <label>Author Name(s) (comma-separated, defaults to your display name)</label>
                  <input type="text" name="writer_name" value={formData.writer_name} onChange={handleFormChange} placeholder={displayName} />
                </div>
                <div className="form-group">
                  <label>Headline Image <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem' }}>(optional — shown on article card)</span></label>
                  <div style={{ fontSize: '0.78rem', color: '#e67e22', marginBottom: '0.35rem', fontWeight: 600 }}>Best results: 1200 × 675 px (16:9 landscape). Portrait or square images will be cropped on feed cards.</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const url = await uploadImage(file, token);
                        setFormData(f => ({ ...f, headline_image: url }));
                      } catch (err) {
                        alert(err.message || 'Image upload failed.');
                      }
                    }}
                  />
                  {formData.headline_image && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src={formData.headline_image} alt="preview" style={{ height: '60px', width: '90px', objectFit: 'cover', borderRadius: '5px', border: '1px solid #ddd' }} />
                      <button type="button" onClick={() => setFormData(f => ({ ...f, headline_image: null }))} style={{ fontSize: '0.78rem', color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>{formMode === 'external' ? 'Teaser / Description' : 'Content'}{formMode === 'external' && <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem', marginLeft: '0.4rem' }}>— shown on the article card before purchase</span>}</label>
                  {formMode === 'external' ? (
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleFormChange}
                      required
                      placeholder="Write a short teaser that readers will see before purchasing access…"
                      style={{ width: '100%', minHeight: '120px', resize: 'vertical' }}
                    />
                  ) : (
                    <ArticleEditor
                      key={`editor-${editingArticle?._id || 'new'}`}
                      initialContent={formData.content}
                      onChange={html => setFormData(f => ({ ...f, content: html }))}
                      placeholder="Write your article..."
                    />
                  )}
                </div>
                <div className="form-group">
                  <GenrePicker
                    key={genrePickerKey}
                    onChange={setGenreTree}
                    initialValue={editingArticle ? (editingArticle.genre_tree || null) : null}
                  />
                </div>
                <div className="form-group">
                  <label>Audience</label>
                  <select name="audience" value={formData.audience} onChange={handleFormChange}>
                    <option value="world">World</option>
                    <option value="national">National</option>
                    <option value="regional">Regional (multi-state)</option>
                    <option value="state">State</option>
                    <option value="local">Local</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Article Credit Price (4–150)</label>
                  <input type="number" name="token_cost" value={formData.token_cost} onChange={handleFormChange} min="4" max="150" step="1" required />
                </div>
                <div className="form-group">
                  <label>Pricing Mode</label>
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginTop: '0.25rem' }}>
                    <label style={{ fontWeight: 'normal', cursor: 'pointer', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input type="radio" value="manual" checked={pricingMode === 'manual'} onChange={() => setPricingMode('manual')} style={{ width: 'auto', padding: 0, margin: 0 }} />
                      Manual
                    </label>
                    <label style={{ fontWeight: 'normal', cursor: 'pointer', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input type="radio" value="auto" checked={pricingMode === 'auto'} onChange={() => setPricingMode('auto')} style={{ width: 'auto', padding: 0, margin: 0 }} />
                      Auto-adjust
                    </label>
                  </div>
                </div>
                {pricingMode === 'auto' && (
                  <div style={{ backgroundColor: '#f8f9fc', border: '1px solid #dde3f0', borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.3rem' }}>First decrease in (days)</div>
                        <input type="number" min="1" value={autoAdjust.firstDecreaseDays} onChange={e => setAutoAdjust(p => ({ ...p, firstDecreaseDays: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '80px' }} />
                        {autoAdjust.firstDecreaseDays > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.2rem' }}>
                            ({new Date(Date.now() + Number(autoAdjust.firstDecreaseDays) * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.3rem' }}>Decrease % each period</div>
                        <input type="number" min="1" max="99" value={autoAdjust.decreasePercent} onChange={e => setAutoAdjust(p => ({ ...p, decreasePercent: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '80px' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.3rem' }}>Frequency (days)</div>
                        <input type="number" min="1" value={autoAdjust.frequencyDays} onChange={e => setAutoAdjust(p => ({ ...p, frequencyDays: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '80px' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.3rem' }}>Skip if avg daily purchases &gt;</div>
                        <input type="number" min="1" value={autoAdjust.skipIfDailyAbove} onChange={e => setAutoAdjust(p => ({ ...p, skipIfDailyAbove: e.target.value }))} placeholder="optional" style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '100px' }} />
                      </div>
                    </div>
                    {autoAdjust.firstDecreaseDays > 0 && autoAdjust.decreasePercent && autoAdjust.frequencyDays && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#4a6fa5', backgroundColor: '#eef2fb', borderRadius: '5px', padding: '0.5rem 0.75rem' }}>
                        Starting {new Date(Date.now() + Number(autoAdjust.firstDecreaseDays) * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}, price decreases {autoAdjust.decreasePercent}% every {autoAdjust.frequencyDays} day{autoAdjust.frequencyDays !== '1' ? 's' : ''}{autoAdjust.skipIfDailyAbove ? ` — skipped if avg daily purchases exceed ${autoAdjust.skipIfDailyAbove}` : ''}. Price floor is 0 tokens (free).
                      </div>
                    )}
                  </div>
                )}
                {editingArticle && editingArticle.purchased > 0 && (
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#7a3e00', fontSize: '0.88rem' }}>
                      Edit Note <span style={{ fontWeight: 400, color: '#888' }}>(required — shown to readers who have purchased this article)</span>
                    </label>
                    <textarea
                      value={editNote}
                      onChange={e => setEditNote(e.target.value)}
                      placeholder="Briefly describe what was changed (e.g. corrected a factual error, updated statistics, added new section)."
                      rows={3}
                      style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #f5c89a', fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>
                )}
                {error && (
                  <div style={{ padding: '0.65rem 0.9rem', marginBottom: '0.75rem', borderRadius: '6px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.45 }}>
                    {error}
                  </div>
                )}
                <button type="button" onClick={() => setShowDraftPreview(true)}>Preview</button>
                <button type="submit">{editingArticle ? 'Save Changes' : 'Submit Article'}</button>
                {!editingArticle && (
                  <button type="button" onClick={(e) => handleSubmit(e, true)} style={{ backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '5px', padding: '0.4rem 0.85rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>Save as Draft</button>
                )}
                <button type="button" onClick={handleCancelForm}>Cancel</button>
              </form>
            </div>
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
              <p>No articles submitted yet.</p>
            </div>
          ) : (
            <div className="article-feed">
              {[...articles].sort((a, b) => {
                if (a.status === 'draft' && b.status !== 'draft') return -1;
                if (b.status === 'draft' && a.status !== 'draft') return 1;
                return new Date(b.date_created) - new Date(a.date_created);
              }).map(article => {
                const isDraft     = article.status === 'draft';
                const statusColor = STATUS_COLOR[article.status] || '#888';
                const statusLabel = STATUS_LABEL[article.status] || article.status;
                const isRejected  = article.status === 'rejected';
                return (
                  <div
                    key={article._id}
                    className="article-card"
                    style={{ borderLeft: `4px solid ${statusColor}`, position: 'relative', backgroundColor: isDraft ? '#fff5f5' : undefined }}
                  >
                    {/* Header row */}
                    <div className="article-header" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                      <div style={{ flex: 1 }}>
                        {article.genre_tree?.main && (
                          <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', letterSpacing: '0.03em' }}>
                            {article.genre_tree.main}{article.genre_tree.sub ? ` / ${article.genre_tree.sub.split(' › ').join(' / ')}` : ''}
                          </div>
                        )}
                        <h3 className="article-title" style={{ margin: 0 }}>{article.title}</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.82rem', color: '#555', marginTop: '0.35rem' }}>
                          <span>Written By: <strong>{Array.isArray(article.writer_name) ? article.writer_name.join(', ') : article.writer_name}</strong></span>
                          <span>Published: {new Date(article.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginTop: '0.1rem' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '700', color: statusColor, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                          {statusLabel}
                        </span>
                        <button
                          onClick={() => setPreviewArticle(article)}
                          style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', backgroundColor: '#f4f4f4', color: '#555', border: '1px solid #ddd', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Preview
                        </button>
                        {!isRejected && (
                          <button
                            onClick={() => openEditForm(article)}
                            style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', backgroundColor: '#fff', color: '#333', border: '1px solid #aaa', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            ✏️ Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="article-meta" style={{ marginTop: '0.4rem' }}>
                      <span className="purchases">🛒 {article.purchased || 0} purchase{article.purchased !== 1 ? 's' : ''}</span>
                      <span style={{ fontSize: '0.82rem', color: '#888' }}>{article.token_cost} tokens</span>
                      {(article.kudos > 0) && (
                        <span style={{ fontSize: '0.82rem', color: '#7b2ff7' }}>👍 {article.kudos}</span>
                      )}
                      {article.wasEdited && article.editedAt && (
                        <span style={{ fontSize: '0.76rem', color: '#888', fontStyle: 'italic' }}>
                          *Edited {new Date(article.editedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </div>

                    {/* Rejection notification */}
                    {isRejected && (
                      <div style={{ marginTop: '0.75rem', backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '7px', padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#dc3545', marginBottom: article.editorNote ? '0.35rem' : 0 }}>
                              ✏️ Revision Requested
                            </div>
                            {article.editorNote && (
                              <div style={{ fontSize: '0.83rem', color: '#555', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                                {article.editorNote}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => openEditForm(article)}
                            style={{ padding: '0.35rem 0.85rem', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            ✏️ Edit Article
                          </button>
                        </div>
                      </div>
                    )}

                    {isDraft && (
                      <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.78rem', color: '#721c24' }}>This article is saved as a draft and is not visible to readers.</span>
                        <button
                          onClick={async () => {
                            try {
                              const authHeader = { Authorization: `Bearer ${token}` };
                              await axios.patch(`/api/articles/${article._id}/status`, { status: 'approved' }, { headers: authHeader });
                              fetchArticles();
                            } catch (err) { setError(err.response?.data?.error || 'Failed to publish.'); }
                          }}
                          style={{ padding: '0.25rem 0.75rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          ✅ Publish Now
                        </button>
                      </div>
                    )}
                    {article.status === 'pending' && (
                      <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#e67e22' }}>
                        Awaiting editor approval before going live.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="sidebar sidebar-right">
          <div className="sidebar-title">Quick Actions</div>
          <div className="recommended-list">
            <div
              className="recommended-item"
              onClick={() => { setEditingArticle(null); setShowForm(true); setSubmittedArticle(null); setError(''); }}
              style={{ cursor: 'pointer' }}
            >
              <span className="recommended-icon">📝</span>
              <span className="recommended-text">Create New Article</span>
            </div>
          </div>
        </div>
      </div>

      {/* Draft preview modal */}
      {showDraftPreview && (
        <DraftPreview
          title={formData.title}
          content={buildPreviewHTML(formData.content)}
          writerName={formData.writer_name}
          genreTree={genreTree}
          tokenCost={formData.token_cost}
          onClose={() => setShowDraftPreview(false)}
        />
      )}

      {/* Article preview modal */}
      {previewArticle && (
        <ArticlePreview
          article={previewArticle}
          onClose={() => setPreviewArticle(null)}
        />
      )}
    </div>
  );
}

export default AuthorDashboard;
